// =============================================================================
// src/views/monitoramento/index.ts — AppModule da Tela 09 (Dashboard de Monitoramento).
//
// Proveniencia: markup FIEL de unified.html (linhas 1157-1340, section
//   data-section="monitoramento") + comportamento portado de Sections.monitoramento
//   (assets/unified-app.js 2033-2115). O markup mora aqui; o comportamento
//   (filtro/contadores/insight/timer-mock) mora em monitoramento-controller.ts.
//
// Divergencia consciente do unified.html (a pedido do usuario, 2026-05-31): numero "09 · "
//   removido do titulo; badge "AO VIVO" recebeu white-space:nowrap/flex-shrink:0/line-height:1
//   para nao quebrar em 2 linhas na coluna estreita.
//
// Ciclo de vida (contrato SectionLifecycleModule): activate(ctx)/deactivate() sao
//   disparados pela SectionManager TS no scroll-spy. init(ctx) cobre o bootstrap.
//   Guardamos ctx.map em activate para deactivate() (sem args) repassar ao controlador.
// =============================================================================

import type { AppContext, MapController } from '../../app/types';
import type { SectionLifecycleModule } from '../../core/section-manager';
import { monitoramentoController } from './monitoramento-controller';
import { buildProvenanceFooterMarkup, buildScaleNoteMarkup } from '../../data/data-provenance';

/** Markup da secao monitoramento (baseado no unified.html, com divergencias conscientes:
 *  titulo sem numero + badge "AO VIVO" anti-quebra — ver bloco de proveniencia acima). */
const MONITORAMENTO_MARKUP = `    <section class="u-section" id="section-monitoramento" data-section="monitoramento">
      <div class="u-section-header">
        <div style="display:flex;align-items:center;gap:8px">
          <div class="u-section-title" style="margin-bottom:0">Dashboard de Monitoramento</div>
          <div style="display:flex;align-items:center;gap:5px;padding:3px 9px;background:#FCEBEB;color:#A32D2D;border-radius:999px;font-size:10px;font-weight:500;white-space:nowrap;flex-shrink:0;line-height:1">
            <div style="width:7px;height:7px;border-radius:50%;background:#E24B4A;animation:pulse 1.5s ease-in-out infinite"></div>
            AO VIVO
          </div>
        </div>
        <div class="u-section-subtitle">Command center para acompanhamento em tempo real no dia da eleição</div>
      </div>
      <div class="u-section-body">
        <div class="u-panel">
          <!-- KPI hero -->
          <div class="u-kpi-hero">
            <div class="u-kpi-label"><i class="ti ti-podium"></i> Apuração parcial — 87%</div>
            <div style="margin-bottom:4px">
              <div class="u-cand-row">
                <div class="u-cand-rank" style="background:#1D9E75">1</div>
                <div class="u-cand-name">D. Almeida <span style="color:var(--color-text-secondary);font-size:10px;font-weight:400">· Avante</span></div>
                <div style="font-weight:500;text-align:right;color:#1D9E75">54,2%</div>
              </div>
              <div style="margin-left:24px;height:4px;background:var(--color-background-tertiary);border-radius:2px;overflow:hidden;margin-top:2px;margin-bottom:6px">
                <div style="width:54.2%;height:100%;background:#1D9E75;border-radius:2px"></div>
              </div>
              <div class="u-cand-row">
                <div class="u-cand-rank" style="background:#185FA5">2</div>
                <div class="u-cand-name">A. Neto <span style="color:var(--color-text-secondary);font-size:10px;font-weight:400">· PL</span></div>
                <div style="font-weight:500;text-align:right;color:#185FA5">39,1%</div>
              </div>
              <div style="margin-left:24px;height:4px;background:var(--color-background-tertiary);border-radius:2px;overflow:hidden;margin-top:2px;margin-bottom:6px">
                <div style="width:39.1%;height:100%;background:#185FA5;border-radius:2px"></div>
              </div>
              <div class="u-cand-row">
                <div class="u-cand-rank" style="background:var(--color-text-tertiary)">3</div>
                <div class="u-cand-name">Brancos/Nulos</div>
                <div style="font-weight:500;text-align:right;color:var(--color-text-secondary)">6,7%</div>
              </div>
            </div>
            <div style="margin-top:8px;padding-top:8px;border-top:0.5px solid var(--color-border-tertiary);display:flex;justify-content:space-between;font-size:11px">
              <span style="color:var(--color-text-secondary)">margem</span>
              <span style="font-weight:500;color:#3B6D11;font-size:13px">+15,1pp</span>
            </div>
          </div>

          <!-- Progress -->
          <div style="background:var(--color-background-secondary);border-radius:var(--border-radius-md);padding:10px 12px">
            <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:6px">
              <span style="color:var(--color-text-secondary)">Apuração</span>
              <!-- Reforma 2026-06 (D3): par verossímil coerente com os 87% da barra
                   (apurados/total = 87,0%) — o valor sequencial anterior era placeholder óbvio. -->
              <span style="font-weight:500">1.242.660 / 1.428.345 votos</span>
            </div>
            <div style="height:8px;background:var(--color-background-tertiary);border-radius:4px;overflow:hidden">
              <div style="width:87%;height:100%;background:#185FA5;border-radius:4px"></div>
            </div>
            <div style="text-align:right;font-size:10px;color:#185FA5;margin-top:3px;font-weight:500">87%</div>
          </div>

          <!-- KPI grid 2x2 -->
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px">
            <div style="background:var(--color-background-secondary);border-radius:var(--border-radius-md);padding:8px 10px">
              <div style="font-size:9px;color:var(--color-text-secondary);margin-bottom:2px">Comparecimento</div>
              <div style="font-size:18px;font-weight:600;color:var(--color-text-primary);line-height:1">86,4%</div>
              <div style="font-size:9px;color:#3B6D11;margin-top:2px"><i class="ti ti-trending-up" aria-hidden="true"></i> alta participação</div>
            </div>
            <div style="background:#FAEEDA;border-radius:var(--border-radius-md);padding:8px 10px">
              <div style="font-size:9px;color:#854F0B;margin-bottom:2px">Alertas ativos</div>
              <div id="u-mon-active-events" style="font-size:18px;font-weight:600;color:#854F0B;line-height:1">8</div>
              <div id="u-mon-critical-count" style="font-size:9px;color:#854F0B;margin-top:2px">3 críticos</div>
            </div>
            <div style="background:#EAF3DE;border-radius:var(--border-radius-md);padding:8px 10px">
              <div style="font-size:9px;color:#3B6D11;margin-bottom:2px">Sentimento</div>
              <div style="font-size:18px;font-weight:600;color:#3B6D11;line-height:1">+0,34</div>
              <div style="font-size:9px;color:#3B6D11;margin-top:2px">positivo</div>
              ${buildScaleNoteMarkup('sentimento')}
            </div>
            <div style="background:var(--color-background-secondary);border-radius:var(--border-radius-md);padding:8px 10px">
              <div style="font-size:9px;color:var(--color-text-secondary);margin-bottom:2px">Menções</div>
              <div style="font-size:18px;font-weight:600;color:var(--color-text-primary);line-height:1">2,8 mil</div>
              <div style="font-size:9px;color:var(--color-text-secondary);margin-top:2px">última hora</div>
            </div>
          </div>

          <!-- Evolution chart -->
          <div>
            <div class="u-panel-section-label">Evolução do dia (06h–23h)</div>
            <svg viewBox="0 0 320 80" style="width:100%;height:80px;display:block">
              <line x1="0" y1="20" x2="320" y2="20" stroke="var(--color-border-tertiary)" stroke-width="0.5"/>
              <line x1="0" y1="40" x2="320" y2="40" stroke="var(--color-border-tertiary)" stroke-width="0.5"/>
              <line x1="0" y1="60" x2="320" y2="60" stroke="var(--color-border-tertiary)" stroke-width="0.5"/>
              <text x="5" y="72" fill="var(--color-text-tertiary)" font-size="7">06h</text>
              <text x="75" y="72" fill="var(--color-text-tertiary)" font-size="7">10h</text>
              <text x="148" y="72" fill="var(--color-text-tertiary)" font-size="7">14h</text>
              <text x="222" y="72" fill="var(--color-text-tertiary)" font-size="7">18h</text>
              <text x="295" y="72" fill="var(--color-text-tertiary)" font-size="7">23h</text>
              <!-- D. Almeida -->
              <polyline points="10,50 80,44 150,38 220,32 295,25" fill="none" stroke="#1D9E75" stroke-width="2" stroke-linejoin="round"/>
              <!-- A. Neto -->
              <polyline points="10,42 80,48 150,50 220,52 295,55" fill="none" stroke="#185FA5" stroke-width="2" stroke-linejoin="round"/>
              <!-- Event markers -->
              <circle cx="150" cy="38" r="3.5" fill="#1D9E75"/>
              <line x1="150" y1="8" x2="150" y2="42" stroke="#BA7517" stroke-width="0.8" stroke-dasharray="2,2"/>
              <text x="153" y="14" fill="#854F0B" font-size="7">boca urna</text>
              <circle cx="220" cy="32" r="3.5" fill="#1D9E75"/>
            </svg>
          </div>

          <!-- Events table -->
          <div>
            <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:6px">
              <div class="u-panel-section-label" style="margin-bottom:0">Eventos recentes</div>
              <div class="u-events-filter">
                <button class="u-event-filter active" data-filter="all">todos · 8</button>
                <button class="u-event-filter" data-filter="crit">críticos · 3</button>
                <button class="u-event-filter" data-filter="warn">alertas · 2</button>
                <button class="u-event-filter" data-filter="event">campanha · 2</button>
                <button class="u-event-filter" data-filter="poll">boca urna · 1</button>
                <button class="u-event-filter" data-filter="sec">segurança · 1</button>
              </div>
            </div>
            <div style="overflow:auto;border:0.5px solid var(--color-border-tertiary);border-radius:var(--border-radius-md)">
              <table style="width:100%;border-collapse:collapse;font-size:11px">
                <thead>
                  <tr style="background:var(--color-background-secondary)">
                    <th style="padding:6px 10px;text-align:left;font-weight:500;font-size:10px;color:var(--color-text-secondary);white-space:nowrap">Hora</th>
                    <th style="padding:6px 10px;text-align:left;font-weight:500;font-size:10px;color:var(--color-text-secondary)">Tipo</th>
                    <th style="padding:6px 10px;text-align:left;font-weight:500;font-size:10px;color:var(--color-text-secondary)">Descrição</th>
                    <th style="padding:6px 10px;text-align:left;font-weight:500;font-size:10px;color:var(--color-text-secondary)">Zona</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="u-live-event-row" data-type="crit" style="border-top:0.5px solid var(--color-border-tertiary)">
                    <td style="padding:6px 10px;color:var(--color-text-secondary);white-space:nowrap">14:23</td>
                    <td style="padding:6px 10px"><span style="background:#FCEBEB;color:#A32D2D;padding:1px 7px;border-radius:999px;font-size:10px">Crítico</span></td>
                    <td style="padding:6px 10px">Urna defeituosa — seção 142-B</td>
                    <td style="padding:6px 10px;color:var(--color-text-secondary)">Zona Norte</td>
                  </tr>
                  <tr class="u-live-event-row" data-type="warn" style="border-top:0.5px solid var(--color-border-tertiary)">
                    <td style="padding:6px 10px;color:var(--color-text-secondary);white-space:nowrap">13:47</td>
                    <td style="padding:6px 10px"><span style="background:#FAEEDA;color:#854F0B;padding:1px 7px;border-radius:999px;font-size:10px">Alerta</span></td>
                    <td style="padding:6px 10px">Fake news viral sobre candidato</td>
                    <td style="padding:6px 10px;color:var(--color-text-secondary)">WhatsApp</td>
                  </tr>
                  <tr class="u-live-event-row" data-type="poll" style="border-top:0.5px solid var(--color-border-tertiary)">
                    <td style="padding:6px 10px;color:var(--color-text-secondary);white-space:nowrap">12:15</td>
                    <td style="padding:6px 10px"><span style="background:#DCECFB;color:#185FA5;padding:1px 7px;border-radius:999px;font-size:10px">Boca urna</span></td>
                    <td style="padding:6px 10px">Boca de urna — Av. Djalma</td>
                    <td style="padding:6px 10px;color:var(--color-text-secondary)">Zona Sul</td>
                  </tr>
                  <tr class="u-live-event-row" data-type="event" style="border-top:0.5px solid var(--color-border-tertiary)">
                    <td style="padding:6px 10px;color:var(--color-text-secondary);white-space:nowrap">11:30</td>
                    <td style="padding:6px 10px"><span style="background:#EAF3DE;color:#3B6D11;padding:1px 7px;border-radius:999px;font-size:10px">Evento</span></td>
                    <td style="padding:6px 10px">Comício surpresa dispersado</td>
                    <td style="padding:6px 10px;color:var(--color-text-secondary)">Centro</td>
                  </tr>
                  <tr class="u-live-event-row" data-type="crit" style="border-top:0.5px solid var(--color-border-tertiary)">
                    <td style="padding:6px 10px;color:var(--color-text-secondary);white-space:nowrap">10:56</td>
                    <td style="padding:6px 10px"><span style="background:#FCEBEB;color:#A32D2D;padding:1px 7px;border-radius:999px;font-size:10px">Crítico</span></td>
                    <td style="padding:6px 10px">Fila acima de 80min — Compensa</td>
                    <td style="padding:6px 10px;color:var(--color-text-secondary)">Zona Oeste</td>
                  </tr>
                  <tr class="u-live-event-row" data-type="sec" style="border-top:0.5px solid var(--color-border-tertiary)">
                    <td style="padding:6px 10px;color:var(--color-text-secondary);white-space:nowrap">10:21</td>
                    <td style="padding:6px 10px"><span style="background:#EFEFFC;color:#534AB7;padding:1px 7px;border-radius:999px;font-size:10px">Segurança</span></td>
                    <td style="padding:6px 10px">Boletim policial em escola municipal</td>
                    <td style="padding:6px 10px;color:var(--color-text-secondary)">Zona Leste</td>
                  </tr>
                  <tr class="u-live-event-row" data-type="warn" style="border-top:0.5px solid var(--color-border-tertiary)">
                    <td style="padding:6px 10px;color:var(--color-text-secondary);white-space:nowrap">09:48</td>
                    <td style="padding:6px 10px"><span style="background:#FAEEDA;color:#854F0B;padding:1px 7px;border-radius:999px;font-size:10px">Alerta</span></td>
                    <td style="padding:6px 10px">Ataque coordenado em grupos locais</td>
                    <td style="padding:6px 10px;color:var(--color-text-secondary)">WhatsApp</td>
                  </tr>
                  <tr class="u-live-event-row" data-type="event" style="border-top:0.5px solid var(--color-border-tertiary)">
                    <td style="padding:6px 10px;color:var(--color-text-secondary);white-space:nowrap">08:35</td>
                    <td style="padding:6px 10px"><span style="background:#EAF3DE;color:#3B6D11;padding:1px 7px;border-radius:999px;font-size:10px">Evento</span></td>
                    <td style="padding:6px 10px">Distribuição de materiais autorizada</td>
                    <td style="padding:6px 10px;color:var(--color-text-secondary)">Zona Sul</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      ${buildProvenanceFooterMarkup('monitoramento')}
    </section>`;

/** Ultimo mapa visto em activate, para deactivate() (sem args) repassar ao controlador. */
let monitoramentoActiveMap: MapController | null = null;

/**
 * AppModule da secao Monitoramento com hooks de ciclo de vida (porte de
 * Sections.monitoramento). render() emite o markup; activate/deactivate sao
 * chamados pela SectionManager no scroll-spy; init(ctx) cobre o bootstrap inicial.
 */
export const monitoramentoView: SectionLifecycleModule = {
  id: 'monitoramento',
  render(): string {
    return MONITORAMENTO_MARKUP;
  },
  // Ativacao dirigida SO pelo SectionManager (scroll-spy). init NAO ativa: senao a
  // secao aplica seus efeitos no mapa compartilhado ja no boot, fora de vista. 2026-05-31.
  init(): void {},
  activate(ctx: AppContext): void {
    if (!ctx.map) return;
    monitoramentoActiveMap = ctx.map;
    monitoramentoController.activate(ctx.map);
  },
  deactivate(): void {
    if (monitoramentoActiveMap) monitoramentoController.deactivate(monitoramentoActiveMap);
  },
};

export default monitoramentoView;
